import { UniButton, UniIconButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { Divider } from '@/design-system/components/Divider';
import { Icon } from '@/design-system/components/icons/Icon';
import { UniInputAmount } from '@/design-system/components/inputs';
import { getObjectScreenNameFromPath, popStateLockedMap } from '@/design-system/components/navigatorEmbed/UtxAppNavigatorEmbed';
import { H6, TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { Pressable, ScrollView, SxProp, View } from 'dripsy';
import _, { debounce, isString } from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { RootStackRoutes } from 'types';
import BillPaymentApp from '@/features/bill-payment/index';
import { BillPaymentStackRoutes } from '@/features/bill-payment/payment.navigator';
import { mapUrlLink, onGoBack } from '@/features/bill-payment/utils/utils';
import { PageHeaderModalNative } from '@/features/bill-payment/screens/components/PageHeaderModalNative';
import { UnionPayStackRoutes } from '@/features/bill-payment/screens/union-pay/type';
import { Step } from '@/features/bill-payment/screens/components/Step';
import { LayoutContext } from 'contexts/LayoutContext';
import { useWallets } from '@/devtools/hooks/useWallets.hook';
import { CurrencyValues } from '@/features/bill-payment/screens/union-pay/options';
import { v4 } from 'uuid';
import { useInstructions, useTransferPeer } from '@/features/bill-payment/hooks/useUnionPay.hook';
import { formatAmountByDecimal } from '@/features/account-management/utils';
import { errorParser } from '@/features/bill-payment/screens/otp/type';
import { CurrenciesIcon } from '@/features/earn/components';
import { UnionType } from '@/features/bill-payment/enum';
import { usePayout } from '@/features/account-management/screens/details/hooks/usePayouts.hook';
import { CountrySelector } from '@/features/card-account/components/countrySelector/CountrySelector';

export interface UnionPayRemittanceNativeProps {
  unionType?: UnionType.PAYMENT | UnionType.ACCOUNT;
  refresh?: () => void;
  navigationNavigate?: string;
  navigationScreen?: string;
}

export const UnionPayRemittanceMobileComponent: React.FC<UnionPayRemittanceNativeProps> = (props) => {
  const {
    unionType,
    refresh,
    navigationNavigate = RootStackRoutes.BillPayment,
    navigationScreen = BillPaymentStackRoutes.UnionPay
  } = props;
  const { t } = useTranslation('bill-payment-union-pay');
  const navigation = useNavigation();
  const navigationAny = useNavigation<any>();
  const layout = useContext(LayoutContext);

  const onBack = (flag = false) => {
    if (Platform.OS === 'web') {
      popStateLockedMap.set('popstate', true);
      if (flag) navigation.getParent()?.goBack();
      else onGoBack(navigation);
      setTimeout(() => popStateLockedMap.set('popstate', false), 500);
    } else {
      if (flag) navigation.getParent()?.goBack();
      else onGoBack(navigation);
    }
  };

  const onPopState = (event: any) => {
    window.history.pushState(event.state, '');
    setTimeout(() => {
      const popStateLocked = popStateLockedMap.get('popstate') ?? false;
      if (!document.location.href || popStateLocked) return;
      const { parentName, subName } = mapUrlLink(document.location.href);
      var screenNameObject = getObjectScreenNameFromPath([BillPaymentApp], `/${subName}`);
      if (
        screenNameObject.screenName &&
        screenNameObject.routeStackName === parentName &&
        screenNameObject.screenName !== BillPaymentStackRoutes.Landing
      ) {
        onBack();
      }
    }, 200);
  };

  useFocusEffect(
    useCallback(() => {
      if (Platform.OS === 'web') {
        window.addEventListener('popstate', onPopState);
        return () => {
          window.removeEventListener('popstate', onPopState);
        };
      }
    }, [])
  );

  const defaultCurrency = 'HKD';
  const defaultDestinationCurrency = 'CNY';
  const [currency, setCurrency] = useState(defaultCurrency);
  const [amount, setAmount] = useState<string | number | undefined>(undefined);
  const [amountRmb, setAmountRmb] = useState<string | number | undefined>(undefined);
  const [wallet, setWallet] = useState<any>(undefined);

  const walletsQuery = useWallets({ enabled: true, noSave: true });
  const wallets = walletsQuery.wallets.data?.data ?? [];
  const walletFlag = wallets.find((w) => w.asset == defaultCurrency);

  useEffect(() => {
    if (!wallet && walletFlag && walletFlag?.wallets) {
      const walletsFlag = walletFlag?.wallets.sort((a, b) => Number(b.balance) - Number(a.balance));
      setWallet(walletsFlag[0]);
    }
  }, [walletFlag]);

  const { runQuote } = useInstructions();
  const [idempotencyKey, setIdempotencyKey] = useState(v4());
  const { runPayoutQuote } = usePayout();
  const postFuncQuote = (
    idempotencyKey: string,
    accountId: string,
    sourceCurrency: string,
    sourceAmount: number,
    destinationCurrency: string,
    destinationAmount: number,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => {
    if (unionType == UnionType.PAYMENT) {
      runQuote(
        {
          intent: 'REMITTANCE',
          idempotencyKey,
          accountId,
          sourceCurrency,
          sourceAmount,
          destinationCurrency,
          destinationAmount,
          method: 'UNIONPAY',
          source: { type: 'ACCOUNT', attributes: { walletId: accountId } }
        },
        onSuccess,
        onError
      );
    } else if (unionType == UnionType.ACCOUNT) {
      runPayoutQuote(
        {
          idempotencyKey,
          accountId,
          sourceCurrency,
          sourceAmount,
          destinationCurrency,
          destinationAmount,
          method: 'UNIONPAY',
          destinationCountry: 'CN'
        },
        onSuccess,
        onError
      );
    }
  };

  const { isLoading, total } = useTransferPeer({ types: ['UNIONPAY'], current: 1, pageSize: 10 });
  const [btnDisabled, setBtnDisabled] = useState(true);
  const [calcFee, setCalcFee] = useState<any>(undefined);

  const [errorMsg, setErrorMsg] = useState<{
    amount: string | undefined;
    amountRmb: string | undefined;
  }>({
    amount: undefined,
    amountRmb: undefined
  });
  const errorMsgFlag = {
    amount: undefined,
    amountRmb: undefined
  };

  const [loading, setLoading] = useState(false);
  const [rateChange, setRateChange] = useState(false);
  const [currencyRmb, setCurrencyRmb] = useState('RMB');

  return (
    <View sx={sxStyles.container}>
      <PageHeaderModalNative>
        <View sx={{ px: '$4', my: '$5' }}>
          <View sx={{ position: 'absolute', top: 0, left: 12, zIndex: 2 }}>
            <UniIconButton icon="arrowLeft" options={{ iconStyles: { themeColor: '$white' } }} onPress={() => onBack()}></UniIconButton>
          </View>
          <View sx={{ textAlign: 'center', width: '100%' }}>
            <TextTitle bold sx={{ color: '$white' }}>
              {t('unionPay::payment-instruction.glossary.unionpay')}
            </TextTitle>
          </View>
          <View sx={{ position: 'absolute', top: 0, right: 12, zIndex: 2 }}>
            <UniIconButton
              icon="rightIcon"
              onPress={() => {
                navigationAny.navigate(navigationNavigate, {
                  screen: navigationScreen,
                  params: { screen: UnionPayStackRoutes.UnionPayMore }
                });
              }}></UniIconButton>
          </View>
        </View>
      </PageHeaderModalNative>
      <ScrollView sx={sxStyles.scrollViewWrapper} contentContainerStyle={{ flexGrow: 1 }} showsVerticalScrollIndicator={false}>
        <View sx={sxStyles.body}>
          <View sx={layout.dimensions.height >= 700 ? { height: layout.dimensions.height - 160 } : { height: calcFee ? 600 : 475 }}>
            <View>
              <View sx={{ alignItems: 'center', gap: '$2' }}>
                <Step step={1} />
                <H6 bold sx={{ color: '$textPrimary', textAlign: 'center', lineHeight: 28 }}>
                  {t('unionPay::payment-instruction.text--how-much-to-send')}
                </H6>
              </View>
              <View sx={{ gap: '$5', backgroundColor: '$backgroundPrimary' }}>
                <View sx={{ gap: '$1', mt: '$5', zIndex: 0 }}>
                  <TextCaption semiBold sx={{ color: '$textPrimary' }}>
                    {t('unionPay::payment-instruction.text--you-send')}
                  </TextCaption>
                  <View
                    sx={{
                      py: '$5',
                      px: '$4',
                      gap: '$4',
                      backgroundColor: '$backgroundPrimary',
                      borderWidth: 1,
                      borderStyle: 'solid',
                      borderColor: '$borderField',
                      borderRadius: '$radius2'
                    }}>
                    <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: '$5' }}>
                      <Pressable
                        disabled={loading || (wallet ? false : true)}
                        onPress={() => {
                          navigationAny.navigate(navigationNavigate, {
                            screen: navigationScreen,
                            params: {
                              screen: UnionPayStackRoutes.AccountSelect,
                              onBack,
                              asset: CurrencyValues,
                              onSelect: (walletId: string, wt?: any) => {
                                if (currency == wt?.asset && wallet?.id == wt?.id) {
                                  return;
                                }

                                setBtnDisabled(true);
                                setCurrency(wt?.asset);
                                setWallet(wt);
                                setCalcFee(undefined);

                                if ((amount || amountRmb) && wt) {
                                  setLoading(true);
                                  if (amount) setAmountRmb('');
                                  postFuncQuote(
                                    idempotencyKey,
                                    wt?.id,
                                    wt?.asset,
                                    amount,
                                    defaultDestinationCurrency,
                                    amount ? undefined : amountRmb,
                                    (data: any) => {
                                      setBtnDisabled(false);
                                      if (unionType == UnionType.PAYMENT) {
                                        setCalcFee(data?.data);
                                        setAmountRmb(data?.data?.attributes?.destination?.amount);
                                      } else if (unionType == UnionType.ACCOUNT) {
                                        let dataFlag = {
                                          ...data?.data,
                                          attributes: { destination: { rate: data?.data?.rate } },
                                          feeCurrency: data?.data?.sourceCurrency,
                                          instructionTotal: data?.data?.sourceAmount
                                        };
                                        setCalcFee(dataFlag);
                                        setAmountRmb(data?.data?.destinationAmount);
                                      }
                                      setLoading(false);

                                      let balanceFlag = 0;
                                      if (unionType == UnionType.PAYMENT) balanceFlag = data?.data?.instructionTotal;
                                      else if (unionType == UnionType.ACCOUNT) balanceFlag = data?.data?.sourceAmount;

                                      if (amount) {
                                        setErrorMsg({
                                          ...errorMsgFlag,
                                          amount: balanceFlag > wt?.balance ? 'Insufficient balance' : undefined
                                        });
                                      } else if (!amount && amountRmb) {
                                        setErrorMsg({
                                          ...errorMsgFlag,
                                          amountRmb: balanceFlag > wt?.balance ? 'Insufficient balance' : undefined
                                        });
                                      }
                                    },
                                    (error: any) => {
                                      setLoading(false);
                                      const data: any = error.response?.data;
                                      const errorMessage =
                                        data && isString(data.message) ? errorParser(data.message, t) : errorParser(error.message, t);
                                      if (amount) {
                                        setErrorMsg({ ...errorMsgFlag, amount: errorMessage });
                                      } else if (!amount && amountRmb) {
                                        setErrorMsg({ ...errorMsgFlag, amountRmb: errorMessage });
                                      }
                                    }
                                  );
                                }
                              }
                            }
                          });
                        }}
                        sx={{ flexDirection: 'row', alignItems: 'center', gap: '$1' }}>
                        <CurrenciesIcon
                          asset={wallet?.asset || currency}
                          textSize="textTitle"
                          textSemiBold
                          textSx={{ color: '$textPrimary' }}
                          textBold={false}
                        />
                        <Icon name="arrowRight" width={24} height={24} />
                      </Pressable>
                      <View>
                        <UniInputAmount
                          decimals={2}
                          assetPrefix={false}
                          maxFlag={false}
                          value={amount}
                          sxWrap={{ border: 0, alignItems: 'center', height: '$7' }}
                          sxInput={{
                            fontWeight: 'bolder',
                            fontSize: '$3',
                            color: '$secondary',
                            textAlign: 'right',
                            width: 120,
                            px: 0
                          }}
                          asset={''}
                          placeholderInput={t('unionPay::payment-instruction.text--enter-amount')}
                          placeholderTextColor="#AFAFAF"
                          editable={!loading && (wallet ? true : false)}
                          onValueChange={debounce((value) => {
                            if (
                              value == undefined ||
                              (value &&
                                amount &&
                                formatAmountByDecimal({ value, decimals: 2 }) == formatAmountByDecimal({ value: amount, decimals: 2 }))
                            ) {
                              return;
                            }

                            setBtnDisabled(true);
                            setAmount(value);
                            setAmountRmb('');

                            if (value && wallet) {
                              setLoading(true);
                              postFuncQuote(
                                idempotencyKey,
                                wallet?.id,
                                currency,
                                value,
                                defaultDestinationCurrency,
                                undefined,
                                (data: any) => {
                                  setBtnDisabled(false);
                                  if (unionType == UnionType.PAYMENT) {
                                    setCalcFee(data?.data);
                                    setAmountRmb(data?.data?.attributes?.destination?.amount);
                                  } else if (unionType == UnionType.ACCOUNT) {
                                    let dataFlag = {
                                      ...data?.data,
                                      attributes: { destination: { rate: data?.data?.rate } },
                                      feeCurrency: data?.data?.sourceCurrency,
                                      instructionTotal: data?.data?.sourceAmount
                                    };
                                    setCalcFee(dataFlag);
                                    setAmountRmb(data?.data?.destinationAmount);
                                  }
                                  setLoading(false);

                                  let balanceFlag = 0;
                                  if (unionType == UnionType.PAYMENT) balanceFlag = data?.data?.instructionTotal;
                                  else if (unionType == UnionType.ACCOUNT) balanceFlag = data?.data?.sourceAmount;

                                  setErrorMsg({
                                    ...errorMsgFlag,
                                    amount: balanceFlag > wallet?.balance ? 'Insufficient balance' : undefined
                                  });
                                },
                                (error: any) => {
                                  setLoading(false);
                                  const data: any = error.response?.data;
                                  const errorMessage =
                                    data && isString(data.message) ? errorParser(data.message, t) : errorParser(error.message, t);
                                  setErrorMsg({ ...errorMsgFlag, amount: errorMessage });
                                }
                              );
                            }
                          }, 1000)}
                          max={wallet && wallet?.balance}
                        />
                      </View>
                    </View>
                    {errorMsg.amount && (
                      <TextCaption danger sx={{ textAlign: 'right' }}>
                        {errorMsg?.amount}
                      </TextCaption>
                    )}
                    <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: '$2' }}>
                      <TextCaption sx={{ color: '$textSecondary' }}>
                        {wallet &&
                          (wallet?.accountLabel ? t(`earn-invest::glossary.${wallet?.accountLabel}`) : wallet?.accountName || ' - ')}
                      </TextCaption>
                      <View sx={{ flexDirection: 'row', gap: 3 }}>
                        <TextCaption sx={{ color: '$textSecondary' }}>{t('unionPay::payment-instruction.glossary.balance')}</TextCaption>
                        <TextCaption
                          sx={{ color: '$textHyperlink', borderBottomWidth: 1, borderBottomColor: '$textHyperlink' }}
                          disabled={loading || (wallet ? false : true)}
                          onPress={() => {
                            if (
                              !wallet?.balance ||
                              (amount &&
                                wallet?.balance &&
                                formatAmountByDecimal({ value: amount, decimals: 2 }) ==
                                  formatAmountByDecimal({ value: wallet?.balance, decimals: 2 }))
                            ) {
                              return;
                            }

                            setBtnDisabled(true);
                            setAmount(wallet?.balance);
                            setCalcFee(undefined);
                            setAmountRmb('');

                            if (wallet && wallet?.balance) {
                              setLoading(true);
                              postFuncQuote(
                                idempotencyKey,
                                wallet?.id,
                                currency,
                                Number(wallet?.balance),
                                defaultDestinationCurrency,
                                undefined,
                                (data: any) => {
                                  setBtnDisabled(false);
                                  if (unionType == UnionType.PAYMENT) {
                                    setCalcFee(data?.data);
                                    setAmountRmb(data?.data?.attributes?.destination?.amount);
                                  } else if (unionType == UnionType.ACCOUNT) {
                                    let dataFlag = {
                                      ...data?.data,
                                      attributes: { destination: { rate: data?.data?.rate } },
                                      feeCurrency: data?.data?.sourceCurrency,
                                      instructionTotal: data?.data?.sourceAmount
                                    };
                                    setCalcFee(dataFlag);
                                    setAmountRmb(data?.data?.destinationAmount);
                                  }
                                  setLoading(false);

                                  let balanceFlag = 0;
                                  if (unionType == UnionType.PAYMENT) balanceFlag = data?.data?.instructionTotal;
                                  else if (unionType == UnionType.ACCOUNT) balanceFlag = data?.data?.sourceAmount;
                                  setErrorMsg({
                                    ...errorMsgFlag,
                                    amount: balanceFlag > wallet?.balance ? 'Insufficient balance' : undefined
                                  });
                                },
                                (error: any) => {
                                  setLoading(false);
                                  const data: any = error.response?.data;
                                  const errorMessage =
                                    data && isString(data.message) ? errorParser(data.message, t) : errorParser(error.message, t);
                                  setErrorMsg({ ...errorMsgFlag, amount: errorMessage });
                                }
                              );
                            }
                          }}>
                          {wallet &&
                            formatAmountByDecimal({
                              value: wallet?.balance,
                              decimals: 2,
                              suffix: wallet?.asset || defaultCurrency
                            })}
                        </TextCaption>
                      </View>
                    </View>
                  </View>
                </View>
                <View sx={{ gap: '$1', zIndex: 1 }}>
                  <TextCaption semiBold sx={{ color: '$textPrimary' }}>
                    {t('unionPay::payment-instruction.text--recipient-receives')}
                  </TextCaption>
                  <View
                    sx={{
                      py: '$5',
                      px: '$4',
                      backgroundColor: '$backgroundPrimary',
                      borderWidth: 1,
                      borderStyle: 'solid',
                      borderColor: '$borderField',
                      borderRadius: '$radius2'
                    }}>
                    <View sx={{ gap: '$4', flexDirection: 'row', justifyContent: 'space-between', height: '$7' }}>
                      <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: '$2' }}>
                        <Icon name="rmb" width={28} height={28} />
                        <TextTitle semiBold sx={{ color: '$textPrimary' }}>
                          {currencyRmb}
                        </TextTitle>
                      </View>
                      <View sx={{ justifyContent: 'center' }}>
                        <UniInputAmount
                          decimals={2}
                          assetPrefix={false}
                          maxFlag={false}
                          value={amountRmb}
                          sxWrap={{ border: 0, alignItems: 'center', height: '$7', justifyContent: 'center' }}
                          sxInput={{
                            fontWeight: 'bolder',
                            fontSize: '$3',
                            color: '$navbarTextInactive',
                            textAlign: 'right',
                            width: 120,
                            px: 0
                          }}
                          asset={''}
                          placeholderInput={t('unionPay::payment-instruction.text--enter-amount')}
                          placeholderTextColor="#AFAFAF"
                          errorText={errorMsg.amountRmb}
                          sx={{ textAlign: 'right' }}
                          editable={!loading && (wallet ? true : false)}
                          onValueChange={debounce((value) => {
                            if (
                              value == undefined ||
                              (value &&
                                amountRmb &&
                                formatAmountByDecimal({ value, decimals: 2 }) == formatAmountByDecimal({ value: amountRmb, decimals: 2 }))
                            ) {
                              return;
                            }

                            setBtnDisabled(true);
                            setAmountRmb(value);
                            setCalcFee(undefined);
                            setAmount('');

                            if (value && wallet) {
                              setLoading(true);
                              postFuncQuote(
                                idempotencyKey,
                                wallet?.id,
                                currency,
                                undefined,
                                defaultDestinationCurrency,
                                value,
                                (data: any) => {
                                  setBtnDisabled(false);
                                  if (unionType == UnionType.PAYMENT) {
                                    setCalcFee(data?.data);
                                    setAmount(data?.data?.attributes?.amount);
                                  } else if (unionType == UnionType.ACCOUNT) {
                                    let dataFlag = {
                                      ...data?.data,
                                      attributes: { destination: { rate: data?.data?.rate } },
                                      feeCurrency: data?.data?.sourceCurrency,
                                      instructionTotal: data?.data?.sourceAmount
                                    };
                                    setCalcFee(dataFlag);
                                    setAmount(data?.data?.sourceAmount);
                                  }
                                  setLoading(false);

                                  let balanceFlag = 0;
                                  if (unionType == UnionType.PAYMENT) balanceFlag = data?.data?.instructionTotal;
                                  else if (unionType == UnionType.ACCOUNT) balanceFlag = data?.data?.sourceAmount;
                                  setErrorMsg({
                                    ...errorMsgFlag,
                                    amountRmb: data?.data?.instructionTotal > wallet?.balance ? 'Insufficient balance' : undefined
                                  });
                                },
                                (error: any) => {
                                  setLoading(false);
                                  const data: any = error.response?.data;
                                  const errorMessage =
                                    data && isString(data.message) ? errorParser(data.message, t) : errorParser(error.message, t);
                                  setErrorMsg({ ...errorMsgFlag, amountRmb: errorMessage });
                                }
                              );
                            }
                          }, 1000)}
                        />
                      </View>
                    </View>
                  </View>
                </View>
                <View
                  sx={{
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 10,
                    position: 'absolute',
                    width: '$12',
                    height: '$12',
                    left: `calc(50% - 48px/2 + 0.5px)`,
                    top: errorMsg.amount ? 188 : 140,
                    backgroundColor: '$backgroundPrimary',
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: '$borderField',
                    borderRadius: 100,
                    zIndex: 2
                  }}>
                  <Icon name="to" width={24} height={24} />
                </View>
              </View>
              {calcFee && calcFee?.attributes?.destination?.rate && (
                <View sx={{ flexDirection: 'row', justifyContent: 'space-between', mt: '$1', alignItems: 'center' }}>
                  <TextBody sx={{ color: '$textSecondary' }}>{t('unionPay::payment-instruction.glossary.conversion-rate')}</TextBody>
                  <View sx={{ gap: '$1', flexDirection: 'row', alignItems: 'center' }}>
                    <TextBody sx={{ color: '$textPrimary' }}>
                      {formatAmountByDecimal({
                        value: rateChange ? calcFee?.attributes?.destination?.rate : 1 / calcFee?.attributes?.destination?.rate,
                        prefix: `1 ${rateChange ? calcFee?.feeCurrency || defaultCurrency : currencyRmb} =`,
                        suffix: rateChange ? currencyRmb : calcFee?.feeCurrency || defaultCurrency,
                        decimals: 4
                      })}
                    </TextBody>
                    <Pressable
                      onPress={() => {
                        setRateChange(!rateChange);
                      }}>
                      <Icon name="swapAccount" width={20} height={20} color="#DC9B32" />
                    </Pressable>
                  </View>
                </View>
              )}
              {calcFee && (
                <View sx={{ backgroundColor: '$backgroundPrimary' }}>
                  <Divider sx={{ margin: 'auto', backgroundColor: '$borderField', width: '100%', mb: '$5', mt: '$10' }} />
                  <View sx={{ gap: '$1' }}>
                    <TextCaption semiBold sx={{ color: '$textPrimary' }}>
                      {t('unionPay::payment-instruction.glossary.debit-amount')}
                    </TextCaption>
                    <H6 bold sx={{ color: '$tagRedBackground' }}>
                      {formatAmountByDecimal({
                        value: calcFee?.instructionTotal,
                        suffix: calcFee?.feeCurrency || defaultCurrency,
                        decimals: 2
                      })}
                    </H6>
                  </View>
                </View>
              )}
            </View>
          </View>
          <View>
            <UniButton
              size="default"
              variant={ButtonVariant.primary}
              loading={loading || (wallet ? false : true)}
              onPress={() => {
                if (total && unionType == UnionType.PAYMENT) {
                  navigationAny.navigate(navigationNavigate, {
                    screen: navigationScreen,
                    params: { screen: UnionPayStackRoutes.Information, onBack, wallet, calcFee }
                  });
                } else {
                  navigationAny.navigate(navigationNavigate, {
                    screen: navigationScreen,
                    params: {
                      screen: UnionPayStackRoutes.AddNewAccount,
                      onBack,
                      transferPeerCountFlag: false,
                      wallet,
                      calcFee,
                      unionType,
                      refresh
                    }
                  });
                }
              }}
              label={t('unionPay::payment-instruction.glossary.continue')}
              disabled={
                !loading &&
                amount &&
                currency &&
                wallet &&
                calcFee &&
                wallet?.balance >= calcFee?.instructionTotal &&
                !btnDisabled &&
                calcFee?.attributes?.destination?.rate
                  ? false
                  : true
              }
              sx={{
                width: '100%',
                height: 44,
                alignItems: 'center',
                justifyContent: 'center',
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.4)',
                my: 20
              }}
            />
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    backgroundColor: '$backgroundPrimary',
    width: '100%',
    height: '100%'
  },
  header: {
    flexDirection: 'column'
  },
  tab: {
    backgroundColor: 'transparent',
    width: '98%'
  },
  body: {
    flex: 1,
    zIndex: 10
  },
  scrollViewWrapper: {
    pt: '$5',
    px: '$4'
  }
};
